import {
  createFileRoute,
  redirect,
  useNavigate,
  useRouteContext,
} from "@tanstack/react-router";
import { useSetAtom } from "jotai";
import { useResetAtom } from "jotai/utils";

import { graphql } from "@repo/graphql-types/gql";
import type { RegulatoryDocumentTypesQueryQuery } from "@repo/graphql-types/graphql";
import CheckList from "@repo/icons/default/CheckList";
import Document from "@repo/icons/default/Document";
import Left from "@repo/icons/default/Left";

import {
  HeaderButton,
  HeaderRoot,
  HeaderTitle,
} from "@/components/ui/layout/header/header";
import {
  MenuList,
  MenuListCard,
  type MenuListItem,
} from "@/components/ui/layout/menu/menu-list";
import { Page, PageDescription } from "@/components/ui/layout/page/page";
import { FullPageSpinner } from "@/components/ui/loading/full-page-spinner";
import { useGraphQL } from "@/hooks/use-graphql";
import { regulatoryDocumentAtom } from "@/lib/atoms/regulatory-document-atom";
import { visibleDocumentTypes } from "@/lib/helpers/regulatory-documents";
import { trackEvent } from "@/lib/tracking";

import type { JSX } from "react";

// biome-ignore lint/plugin/route-file-exports: predates the rule; move it out
export const RegulatoryDocumentTypesQuery = graphql(/* GraphQL */ `
  query RegulatoryDocumentTypesQuery {
    tiposDocumentosRegulatorios {
      codTipoDocumentoRegulatorio
      nome
      descricao
    }
  }
`);

type RegulatoryDocumentType = NonNullable<
  NonNullable<RegulatoryDocumentTypesQueryQuery["tiposDocumentosRegulatorios"]>[number]
>;

const RegulatoryDocumentsPage = (): JSX.Element => {
  const navigate = useNavigate();
  const resetDraft = useResetAtom(regulatoryDocumentAtom);
  const setDraft = useSetAtom(regulatoryDocumentAtom);

  const { flags } = useRouteContext({ from: "__root__" });
  const declarationEnabled = Boolean(
    flags["exibe-documentos-regulatorios-de-declaracao-de-vinculos"],
  );

  const { data, isPending } = useGraphQL(RegulatoryDocumentTypesQuery, {});
  const documentTypes = visibleDocumentTypes(
    (data?.tiposDocumentosRegulatorios ?? []).filter(
      (documentType): documentType is RegulatoryDocumentType => documentType !== null,
    ),
    declarationEnabled,
  );

  const handleSelectDocument = (documentType: RegulatoryDocumentType): void => {
    resetDraft();
    setDraft((prev) => ({
      ...prev,
      codTipoDocumentoRegulatorio: documentType.codTipoDocumentoRegulatorio,
      documentName: documentType.nome,
    }));

    trackEvent("Documento Regulatório Selecionado", {
      codTipoDocumentoRegulatorio: documentType.codTipoDocumentoRegulatorio,
      nome: documentType.nome,
    });

    void navigate({ to: "/settings/regulatory-documents/reason" });
  };

  const documentMenuItems: MenuListItem[] = documentTypes.map((documentType) => ({
    name: documentType.nome,
    Icon: Document,
    description: documentType.descricao ?? undefined,
    onClick: () => {
      handleSelectDocument(documentType);
    },
  }));

  return (
    <>
      <HeaderRoot>
        <HeaderButton icon={Left} align="start" />
        <HeaderTitle title="Documentos Regulatórios" align="center" />
      </HeaderRoot>
      <Page showCustomerIoContainer={false}>
        <div className="mb-4">
          <PageDescription>
            Baixe os documentos regulatórios das unidades em PDF ou solicite sua
            Declaração de Vínculo com a Livance.
          </PageDescription>
        </div>

        {isPending ? (
          <FullPageSpinner />
        ) : (
          <>
            <MenuListCard>
              <MenuList menuItems={documentMenuItems} />
            </MenuListCard>

            <div className="mt-4">
              <MenuListCard>
                <MenuList
                  menuItems={[
                    {
                      name: "Minhas solicitações",
                      Icon: CheckList,
                      description: "Acompanhe o status do que você já pediu",
                      onClick: () => {
                        void navigate({ to: "/settings/regulatory-documents/requests" });
                      },
                    },
                  ]}
                />
              </MenuListCard>
            </div>
          </>
        )}
      </Page>
    </>
  );
};

export const Route = createFileRoute("/settings/regulatory-documents/")({
  component: RegulatoryDocumentsPage,
  beforeLoad: ({ context }) => {
    if (!context.flags["exibe-documentos-regulatorios-no-app"]) {
      redirect({ to: "/settings", throw: true });
    }

    if (!context.user.possuiAgendaAtiva) {
      redirect({ to: "/settings", throw: true });
    }
  },
});
